import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import PageHero from "@/components/PageHero";
import { Link } from "react-router-dom";
import { COMPANY, LOGO_URL } from "@/lib/brand";
import { usePageMeta } from "@/lib/seo";
import {
  Heart,
  Handshake,
  Phone,
  ArrowRight,
  ClipboardList,
  Camera,
  CalendarCheck,
  Truck,
  ShieldCheck,
  Recycle,
  Clock,
  Users,
} from "lucide-react";

const process = [
  {
    icon: Phone,
    title: "A quiet first conversation",
    body:
      "Call, email or start a booking whenever you feel ready. There's no script and no rush — tell us as much or as little as you'd like about the property and what needs clearing.",
  },
  {
    icon: Camera,
    title: "A gentle look at the property",
    body:
      "Send us photos of the rooms through the booking form, or ask us to visit. Nothing is moved or touched until we've agreed a plan with you.",
  },
  {
    icon: ClipboardList,
    title: "Keepsakes set aside first",
    body:
      "Before we clear a single item, we walk through with you (or a family member) to identify anything you'd like to keep, donate or pass on. Photos, papers and small personal items are always set aside carefully.",
  },
  {
    icon: CalendarCheck,
    title: "A date that suits the family",
    body:
      "We schedule around solicitors, executors, estate agents and family. Just choose a preferred date that works best for you.",
  },
  {
    icon: Truck,
    title: "A quiet, respectful clearance",
    body:
      "Our small family team clears the property calmly and thoroughly. Neighbours barely notice we're there. Everything is disposed of properly, and we recycle or donate wherever we can.",
  },
];

const promises = [
  { icon: Heart, title: "Treated with dignity", body: "Every home is handled the way we'd want our own family's home to be handled." },
  { icon: Handshake, title: "No pressure, ever", body: "Take your time. Ask any question. Change your mind. We work at your pace." },
  { icon: ShieldCheck, title: "Fully insured & EA registered", body: "Environment Agency registered waste carriers. Full public liability insurance." },
  { icon: Users, title: "The same family throughout", body: "You'll deal with the same faces from the first call to the final sweep-up." },
  { icon: Recycle, title: "Recycled &amp; donated first", body: "Furniture, clothes and useable goods go to local charities and reuse schemes wherever possible." },
  { icon: Clock, title: "Around your timeline", body: "Solicitors, executors, agents — we co-ordinate so you don't have to." },
];

export default function ProbateClearances() {
  usePageMeta(
    "Probate & Bereavement Clearance Peterborough | Waites Clearances",
    "Respectful probate and bereavement house clearances in Peterborough. A caring, family run team based in Peterborough, working with families, solicitors and executors at your pace."
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="page-probate">
      <Header />

      <PageHero
        eyebrow="Probate & bereavement clearances in Peterborough"
        title="Handled with the care they deserve."
        description="Clearing a loved one's home is never easy. Waites Clearances is a family run team based in Peterborough, and we approach every probate or bereavement clearance quietly, respectfully and at your pace. There's no pressure, no judgment, and no detail too small."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/book"
            data-testid="probate-cta-book"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors"
          >
            Get a Free Quote <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="probate-cta-phone"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-200 bg-white text-slate-800 hover:border-[#FF7F00] hover:text-[#FF7F00] font-semibold transition-colors"
          >
            <Phone className="h-4 w-4 text-[#FF7F00]" /> Prefer to talk? Call {COMPANY.phoneDisplay}
          </a>
        </div>
      </PageHero>

      {/* Opening reassurance block */}
      <section className="py-16 sm:py-20" data-testid="probate-intro">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <div className="rounded-3xl border border-[#FFB870] bg-gradient-to-br from-[#FFF7EE] to-white p-8 sm:p-12">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-white border border-[#FFB870] flex items-center justify-center shrink-0">
                <Heart className="h-6 w-6 text-[#FF7F00]" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B85800]">
                  A message from our family
                </p>
                <p className="mt-1 font-display text-lg font-bold text-slate-900">
                  We understand.
                </p>
              </div>
            </div>
            <p className="mt-6 text-slate-700 leading-relaxed">
              We've been through it ourselves. Sorting through a family member's belongings is
              emotional work, and it can feel overwhelming trying to do it while also managing
              solicitors, executors, funerals and everything else that comes with losing someone.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              You don't have to do the practical side alone. When you're ready — and only when
              you're ready — we'll help you clear the property calmly and thoroughly, so you can
              focus on what matters.
            </p>
          </div>
        </div>
      </section>

      {/* Two-service cards */}
      <section className="pb-16" data-testid="probate-services">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 grid sm:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-slate-200 bg-white p-8" data-testid="probate-service-probate">
            <div className="h-12 w-12 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center mb-5">
              <Heart className="h-6 w-6 text-[#FF7F00]" />
            </div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900 tracking-tight">
              Probate clearances
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              A discreet, thorough clearance of the estate, anywhere in Peterborough and nearby. We can inventory items where you need
              it, co-ordinate with solicitors and executors, and time the work around a property
              sale so keys can be handed over the moment we're done.
            </p>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-8" data-testid="probate-service-bereavement">
            <div className="h-12 w-12 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center mb-5">
              <Handshake className="h-6 w-6 text-[#FF7F00]" />
            </div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900 tracking-tight">
              Bereavement clearances
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              A gentle, patient service for families who need a hand clearing a loved one's home.
              Personal items and keepsakes are always set aside with you first, and nothing is
              rushed. We work to your pace, never ours.
            </p>
          </div>
        </div>
      </section>

      {/* Our process */}
      <section className="py-20 sm:py-24 bg-white border-y border-slate-100" data-testid="probate-process">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">Our process</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              What to expect, step by step.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              You'll never be surprised by anything we do. Here's exactly how a probate or
              bereavement clearance runs with us.
            </p>
          </div>

          <ol className="mt-12 space-y-4">
            {process.map(({ icon: Icon, title, body }, i) => (
              <li
                key={title}
                data-testid={`probate-step-${i + 1}`}
                className="flex gap-5 rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 hover:border-[#FFB870] transition-colors"
              >
                <span className="h-11 w-11 rounded-xl bg-[#0F172A] text-white text-sm font-black flex items-center justify-center shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-[#FF7F00]" />
                    <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
                  </div>
                  <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Our promises */}
      <section className="py-20 sm:py-24" data-testid="probate-promises">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">Our promises</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              How we look after you.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {promises.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                data-testid={`probate-promise-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#FFB870] hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="h-11 w-11 rounded-xl bg-[#FFF3E6] flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-[#FF7F00]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
                <p
                  className="mt-2 text-sm text-slate-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working with solicitors */}
      <section className="pb-20" data-testid="probate-solicitors">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-12">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">
              For solicitors &amp; executors
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
              Working alongside the estate.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-3xl">
              We regularly co-ordinate with solicitors, executors and estate agents on probate
              matters across Peterborough and the surrounding areas — happy to work around key handovers, viewings and completion dates. We can
              provide a written summary of items cleared and photographic records where required.
              Get in touch and we'll fit around your timeline.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                href={`tel:${COMPANY.phoneTel}`}
                data-testid="probate-solicitors-phone"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#0F172A] hover:bg-[#1F2937] text-white font-bold transition-colors"
              >
                <Phone className="h-4 w-4" /> Call {COMPANY.phoneDisplay}
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="probate-solicitors-email"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-200 bg-white text-slate-800 hover:border-[#FF7F00] hover:text-[#FF7F00] font-semibold transition-colors"
              >
                {COMPANY.email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Closing quote card */}
      <section className="pb-24" data-testid="probate-quote">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <div className="rounded-3xl border border-[#FFB870] bg-gradient-to-br from-[#FFF7EE] to-white p-8 sm:p-12">
            <div className="flex items-center gap-4">
              <img
                src={LOGO_URL}
                alt="Waites Clearances logo"
                className="h-16 w-16 rounded-2xl object-contain bg-white border border-slate-200"
              />
              <div>
                <p className="font-display font-extrabold text-slate-900 leading-tight">
                  Waites <span className="text-[#FF7F00]">Clearances</span>
                </p>
                <p className="text-sm text-slate-500">Family run in Peterborough</p>
              </div>
            </div>
            <blockquote className="mt-6 font-display text-xl sm:text-2xl italic text-slate-800 leading-snug">
              "There's no rush, and no wrong questions. When you're ready, we're here — and we'll
              treat their home like it was our own family's."
            </blockquote>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/book"
                data-testid="probate-quote-cta-book"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors"
              >
                Get a Free Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneTel}`}
                data-testid="probate-quote-cta-phone"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-200 bg-white text-slate-800 hover:border-[#FF7F00] hover:text-[#FF7F00] font-semibold transition-colors"
              >
                <Phone className="h-4 w-4 text-[#FF7F00]" /> Call {COMPANY.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
